import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScienceIcon from "@mui/icons-material/Science";

const EligibilityCheckerPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    age: "",
    gender: "",
    state: "",
    willingToTravel: false,
    maxTravelDistance: "50",

    // Step 2: Medical History
    ckdEtiology: "",
    diagnosisConfirmed: false,
    diagnosisDate: "",
    ckdStage: "",
    previousTreatment: false,
    previousTreatmentType: [],

    // Step 3: Current Treatment
    currentMedications: [],
    immunosuppressiveTherapy: false,
    onDialysis: false,
    kidneyTransplant: false,

    // Step 4: CKD Complications
    ckdComplications: [],
    anemiaSymptoms: false,
    egfr: "",
    boneMineralSymptoms: false,
    cardiovascularSymptoms: false,

    // Step 5: Lab Results (Optional)
    uacr: "",
    potassium: "",
    hemoglobin: "",
    pth: "",
  });

  const [results, setResults] = useState(null);
  const [matchedTrials, setMatchedTrials] = useState([]);

  const steps = [
    "Basic Information",
    "Medical History",
    "Current Treatment",
    "CKD Complications",
    "Results",
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleArrayToggle = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((v) => v !== value)
        : [...prev[field], value],
    }));
  };

  const handleNext = () => {
    if (activeStep === steps.length - 2) {
      // Calculate results before showing final step
      calculateEligibility();
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setFormData({
      age: "",
      gender: "",
      state: "",
      willingToTravel: false,
      maxTravelDistance: "50",
      ckdEtiology: "",
      diagnosisConfirmed: false,
      diagnosisDate: "",
      ckdStage: "",
      previousTreatment: false,
      previousTreatmentType: [],
      currentMedications: [],
      immunosuppressiveTherapy: false,
      onDialysis: false,
      kidneyTransplant: false,
      ckdComplications: [],
      anemiaSymptoms: false,
      egfr: "",
      boneMineralSymptoms: false,
      cardiovascularSymptoms: false,
      uacr: "",
      potassium: "",
      hemoglobin: "",
      pth: "",
    });
    setResults(null);
    setMatchedTrials([]);
  };

  const calculateEligibility = () => {
    // Eligibility scoring algorithm
    let totalScore = 0;
    let maxScore = 0;
    let eligibilityCriteria = {
      age: { met: false, weight: 10 },
      diagnosis: { met: false, weight: 20 },
      ckdEtiology: { met: false, weight: 15 },
      complications: { met: false, weight: 15 },
      priorTreatment: { met: false, weight: 10 },
      labValues: { met: false, weight: 10 },
      location: { met: false, weight: 10 },
      generalHealth: { met: false, weight: 10 },
    };

    // Age check (18-85 is typical range)
    maxScore += eligibilityCriteria.age.weight;
    if (formData.age >= 18 && formData.age <= 85) {
      totalScore += eligibilityCriteria.age.weight;
      eligibilityCriteria.age.met = true;
    }

    // Confirmed diagnosis
    maxScore += eligibilityCriteria.diagnosis.weight;
    if (formData.diagnosisConfirmed) {
      totalScore += eligibilityCriteria.diagnosis.weight;
      eligibilityCriteria.diagnosis.met = true;
    }

    // CKD etiology specified
    maxScore += eligibilityCriteria.ckdEtiology.weight;
    if (formData.ckdEtiology) {
      totalScore += eligibilityCriteria.ckdEtiology.weight;
      eligibilityCriteria.ckdEtiology.met = true;
    }

    // Complications documented
    maxScore += eligibilityCriteria.complications.weight;
    if (formData.ckdComplications.length > 0) {
      totalScore += eligibilityCriteria.complications.weight;
      eligibilityCriteria.complications.met = true;
    }

    // Prior treatment history
    maxScore += eligibilityCriteria.priorTreatment.weight;
    if (formData.previousTreatment !== undefined) {
      totalScore += eligibilityCriteria.priorTreatment.weight;
      eligibilityCriteria.priorTreatment.met = true;
    }

    // Lab values (optional but helpful)
    maxScore += eligibilityCriteria.labValues.weight;
    if (
      formData.egfr ||
      formData.uacr ||
      formData.potassium ||
      formData.hemoglobin
    ) {
      totalScore += eligibilityCriteria.labValues.weight;
      eligibilityCriteria.labValues.met = true;
    }

    // Location/travel willingness
    maxScore += eligibilityCriteria.location.weight;
    if (formData.state || formData.willingToTravel) {
      totalScore += eligibilityCriteria.location.weight;
      eligibilityCriteria.location.met = true;
    }

    // General health (no prior kidney transplant, eGFR not below dialysis threshold)
    maxScore += eligibilityCriteria.generalHealth.weight;
    if (!formData.kidneyTransplant && Number(formData.egfr || 100) >= 15) {
      totalScore += eligibilityCriteria.generalHealth.weight;
      eligibilityCriteria.generalHealth.met = true;
    }

    const percentageScore = Math.round((totalScore / maxScore) * 100);

    // Determine eligibility category
    let category = "low";
    let categoryLabel = "Low Match";
    let categoryColor = "error";

    if (percentageScore >= 80) {
      category = "high";
      categoryLabel = "Highly Eligible";
      categoryColor = "success";
    } else if (percentageScore >= 60) {
      category = "moderate";
      categoryLabel = "Potentially Eligible";
      categoryColor = "warning";
    }

    // Generate mock matched trials based on criteria
    const mockTrials = generateMockMatches();

    setResults({
      score: percentageScore,
      category,
      categoryLabel,
      categoryColor,
      criteria: eligibilityCriteria,
      totalScore,
      maxScore,
    });

    setMatchedTrials(mockTrials);
  };

  const generateMockMatches = () => {
    // This would normally call the backend matching API
    const trials = [];

    if (
      formData.ckdEtiology === "Diabetic Nephropathy" &&
      formData.ckdComplications.includes("Cardiovascular")
    ) {
      trials.push({
        nctId: "NCT04819035",
        title: "SGLT2 Inhibitor Cardiorenal Outcomes in Diabetic Nephropathy",
        phase: "Phase 3",
        status: "Recruiting",
        matchScore: 92,
        reason: "Matches diabetic nephropathy etiology and cardiovascular involvement",
      });
    }

    if (formData.ckdEtiology === "Polycystic Kidney Disease") {
      trials.push({
        nctId: "NCT05027602",
        title: "Next-Generation Cyst-Targeting Therapy for ADPKD",
        phase: "Phase 2",
        status: "Recruiting",
        matchScore: 88,
        reason: "Matches polycystic kidney disease etiology",
      });
    }

    if (formData.ckdEtiology === "Glomerulonephritis") {
      trials.push({
        nctId: "NCT05123890",
        title: "Targeted Complement Inhibition for IgA Nephropathy",
        phase: "Phase 2",
        status: "Recruiting",
        matchScore: 85,
        reason: "Matches glomerulonephritis etiology",
      });
    }

    if (formData.ckdComplications.includes("Anemia")) {
      trials.push({
        nctId: "NCT04756891",
        title: "Oral HIF-PH Inhibitor for CKD-Related Anemia",
        phase: "Phase 3",
        status: "Not Yet Recruiting",
        matchScore: 78,
        reason: "Matches anemia complication",
      });
    }

    if (!formData.previousTreatment) {
      trials.push({
        nctId: "NCT05198765",
        title: "First-Line Kidney-Protective Therapy for Newly Diagnosed CKD",
        phase: "Phase 3",
        status: "Recruiting",
        matchScore: 82,
        reason: "Accepting treatment-naive patients",
      });
    }

    return trials;
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return formData.age && formData.gender && formData.state;
      case 1:
        return formData.ckdEtiology && formData.diagnosisConfirmed;
      case 2:
        return true; // Optional step
      case 3:
        return formData.ckdComplications.length > 0;
      default:
        return true;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Age"
                type="number"
                required
                value={formData.age}
                onChange={(e) => handleInputChange("age", e.target.value)}
                helperText="Most trials accept ages 18-85"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={formData.gender}
                  label="Gender"
                  onChange={(e) => handleInputChange("gender", e.target.value)}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>State</InputLabel>
                <Select
                  value={formData.state}
                  label="State"
                  onChange={(e) => handleInputChange("state", e.target.value)}
                >
                  <MenuItem value="CA">California</MenuItem>
                  <MenuItem value="NY">New York</MenuItem>
                  <MenuItem value="TX">Texas</MenuItem>
                  <MenuItem value="FL">Florida</MenuItem>
                  <MenuItem value="IL">Illinois</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Travel Distance (miles)"
                type="number"
                value={formData.maxTravelDistance}
                onChange={(e) =>
                  handleInputChange("maxTravelDistance", e.target.value)
                }
                helperText="How far you're willing to travel"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.willingToTravel}
                    onChange={(e) =>
                      handleInputChange("willingToTravel", e.target.checked)
                    }
                  />
                }
                label="I am willing to travel out of state for the right trial"
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Medical History
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>CKD Etiology</InputLabel>
                <Select
                  value={formData.ckdEtiology}
                  label="CKD Etiology"
                  onChange={(e) =>
                    handleInputChange("ckdEtiology", e.target.value)
                  }
                >
                  <MenuItem value="Diabetic Nephropathy">Diabetic Nephropathy</MenuItem>
                  <MenuItem value="Hypertensive Nephrosclerosis">Hypertensive Nephrosclerosis</MenuItem>
                  <MenuItem value="Glomerulonephritis">Glomerulonephritis</MenuItem>
                  <MenuItem value="Polycystic Kidney Disease">Polycystic Kidney Disease</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                  <MenuItem value="Unknown">Unknown/Not Sure</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Diagnosis Date"
                type="date"
                value={formData.diagnosisDate}
                onChange={(e) =>
                  handleInputChange("diagnosisDate", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.diagnosisConfirmed}
                    onChange={(e) =>
                      handleInputChange("diagnosisConfirmed", e.target.checked)
                    }
                  />
                }
                label="My CKD diagnosis has been confirmed by my nephrologist"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  CKD Stage (if known)
                </FormLabel>
                <RadioGroup
                  value={formData.ckdStage}
                  onChange={(e) =>
                    handleInputChange("ckdStage", e.target.value)
                  }
                >
                  <FormControlLabel value="1" control={<Radio />} label="Stage 1 (eGFR ≥ 90)" />
                  <FormControlLabel value="2" control={<Radio />} label="Stage 2 (eGFR 60-89)" />
                  <FormControlLabel value="3a" control={<Radio />} label="Stage 3a (eGFR 45-59)" />
                  <FormControlLabel value="3b" control={<Radio />} label="Stage 3b (eGFR 30-44)" />
                  <FormControlLabel value="4" control={<Radio />} label="Stage 4 (eGFR 15-29)" />
                  <FormControlLabel value="5" control={<Radio />} label="Stage 5 (eGFR < 15)" />
                  <FormControlLabel value="5D" control={<Radio />} label="Stage 5D (Dialysis-dependent)" />
                  <FormControlLabel value="unknown" control={<Radio />} label="Unknown" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.previousTreatment}
                    onChange={(e) =>
                      handleInputChange("previousTreatment", e.target.checked)
                    }
                  />
                }
                label="I have received treatment for my kidney disease"
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Current Treatment
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Alert severity="info" sx={{ mb: 3 }}>
                This information helps match you to trials that accept patients
                on specific medications or treatment status.
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.immunosuppressiveTherapy}
                    onChange={(e) =>
                      handleInputChange("immunosuppressiveTherapy", e.target.checked)
                    }
                  />
                }
                label="I have received immunosuppressive therapy (e.g. for glomerulonephritis)"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.onDialysis}
                    onChange={(e) =>
                      handleInputChange("onDialysis", e.target.checked)
                    }
                  />
                }
                label="I am currently on dialysis (hemodialysis or peritoneal dialysis)"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.kidneyTransplant}
                    onChange={(e) =>
                      handleInputChange("kidneyTransplant", e.target.checked)
                    }
                  />
                }
                label="I have had a kidney transplant"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Current Medications (Select all that apply)
              </Typography>
              <Grid container spacing={1}>
                {[
                  "ACE Inhibitor / ARB",
                  "SGLT2 Inhibitor",
                  "Finerenone",
                  "Immunosuppressant",
                  "Phosphate Binder",
                  "ESA (Erythropoiesis-Stimulating Agent)",
                  "Tolvaptan",
                  "None",
                ].map((med) => (
                  <Grid item xs={12} sm={6} key={med}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.currentMedications.includes(med)}
                          onChange={() =>
                            handleArrayToggle("currentMedications", med)
                          }
                        />
                      }
                      label={med}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                CKD Complications
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Alert severity="info" sx={{ mb: 3 }}>
                Many trials target specific complications of CKD. Select all
                that apply to you.
              </Alert>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Most Recent eGFR (mL/min/1.73m²)"
                type="number"
                value={formData.egfr}
                onChange={(e) => handleInputChange("egfr", e.target.value)}
                helperText="Leave blank if you don't know your latest value"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Complications (Select all that apply)
              </Typography>
              <Grid container spacing={1}>
                {[
                  "Anemia",
                  "Fluid Overload",
                  "Bone/Mineral Disorder",
                  "Cardiovascular",
                  "Hyperkalemia",
                  "Metabolic Acidosis",
                  "Neuropathy",
                ].map((complication) => (
                  <Grid item xs={12} sm={6} md={4} key={complication}>
                    <Chip
                      label={complication}
                      onClick={() => handleArrayToggle("ckdComplications", complication)}
                      color={
                        formData.ckdComplications.includes(complication)
                          ? "primary"
                          : "default"
                      }
                      variant={
                        formData.ckdComplications.includes(complication)
                          ? "filled"
                          : "outlined"
                      }
                      sx={{ width: "100%", cursor: "pointer" }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {formData.ckdComplications.includes("Anemia") && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.anemiaSymptoms}
                      onChange={(e) =>
                        handleInputChange("anemiaSymptoms", e.target.checked)
                      }
                    />
                  }
                  label="I have anemia symptoms (fatigue, shortness of breath, pale skin)"
                />
              </Grid>
            )}

            {formData.ckdComplications.includes("Bone/Mineral Disorder") && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.boneMineralSymptoms}
                      onChange={(e) =>
                        handleInputChange(
                          "boneMineralSymptoms",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label="I have bone pain or have been told I have elevated PTH/phosphorus"
                />
              </Grid>
            )}

            {formData.ckdComplications.includes("Cardiovascular") && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.cardiovascularSymptoms}
                      onChange={(e) =>
                        handleInputChange(
                          "cardiovascularSymptoms",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label="I have cardiovascular symptoms (shortness of breath, swelling, high blood pressure)"
                />
              </Grid>
            )}
          </Grid>
        );

      case 4:
        return (
          <Box>
            {results && (
              <>
                {/* Overall Score */}
                <Card
                  elevation={3}
                  sx={{
                    mb: 3,
                    background:
                      "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                  }}
                >
                  <CardContent>
                    <Box sx={{ textAlign: "center", py: 2 }}>
                      <Typography
                        variant="h3"
                        fontWeight="bold"
                        color={`${results.categoryColor}.main`}
                      >
                        {results.score}%
                      </Typography>
                      <Typography variant="h5" gutterBottom>
                        {results.categoryLabel}
                      </Typography>
                      <Chip
                        label={results.categoryLabel}
                        color={results.categoryColor}
                        icon={
                          results.score >= 80 ? (
                            <CheckCircleIcon />
                          ) : results.score >= 60 ? (
                            <WarningIcon />
                          ) : (
                            <CancelIcon />
                          )
                        }
                        sx={{ mt: 1 }}
                      />
                    </Box>

                    <Box sx={{ mt: 3 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Eligibility Score: {results.totalScore} /{" "}
                        {results.maxScore} points
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={results.score}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          bgcolor: "rgba(0,0,0,0.1)",
                          "& .MuiLinearProgress-bar": {
                            background:
                              results.score >= 80
                                ? "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                                : results.score >= 60
                                ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                                : "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                          },
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>

                {/* Criteria Breakdown */}
                <Paper elevation={2} sx={{ mb: 3, p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Eligibility Criteria Breakdown
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List>
                    {Object.entries(results.criteria).map(([key, value]) => (
                      <ListItem key={key}>
                        <ListItemIcon>
                          {value.met ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <CancelIcon color="error" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            key.charAt(0).toUpperCase() +
                            key.slice(1).replace(/([A-Z])/g, " $1")
                          }
                          secondary={`${value.weight} points ${
                            value.met ? "(Met)" : "(Not Met)"
                          }`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>

                {/* Matched Trials */}
                <Paper elevation={2} sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Potentially Matching Trials ({matchedTrials.length})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {matchedTrials.length > 0 ? (
                    matchedTrials.map((trial, index) => (
                      <Accordion key={index} elevation={1}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              width: "100%",
                            }}
                          >
                            <ScienceIcon color="primary" />
                            <Box sx={{ flex: 1 }}>
                              <Typography fontWeight="bold">
                                {trial.title}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {trial.nctId} • {trial.phase} • {trial.status}
                              </Typography>
                            </Box>
                            <Chip
                              label={`${trial.matchScore}% Match`}
                              color={
                                trial.matchScore >= 85 ? "success" : "warning"
                              }
                              size="small"
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography variant="body2" paragraph>
                            <strong>Why this matches:</strong> {trial.reason}
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            href={`https://clinicaltrials.gov/study/${trial.nctId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View on ClinicalTrials.gov
                          </Button>
                        </AccordionDetails>
                      </Accordion>
                    ))
                  ) : (
                    <Alert severity="info">
                      No trials currently match your criteria. Try adjusting
                      your responses or check back later as new trials are added
                      regularly.
                    </Alert>
                  )}
                </Paper>

                {/* Disclaimer */}
                <Alert severity="warning" sx={{ mt: 3 }}>
                  <strong>Important:</strong> This is a preliminary screening
                  tool only. Final eligibility is determined by the trial
                  coordinator after reviewing your complete medical history.
                  Always consult with your healthcare provider before enrolling
                  in a clinical trial.
                </Alert>
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <FactCheckIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h3" fontWeight="bold">
            Eligibility Checker
          </Typography>
          <Chip label="Premium" color="warning" icon={<CheckCircleIcon />} />
        </Box>
        <Typography variant="h6" color="text.secondary">
          Quick pre-screening for clinical trial eligibility
        </Typography>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Answer a few questions to see which clinical trials you might be
        eligible for. This takes about 5 minutes and provides instant results.
      </Alert>

      {/* Stepper */}
      <Paper elevation={3} sx={{ p: 4 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step Content */}
        <Box sx={{ minHeight: 400 }}>{renderStepContent()}</Box>

        {/* Navigation Buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button
            variant="outlined"
            onClick={activeStep === 0 ? handleReset : handleBack}
            startIcon={
              activeStep === 0 ? <RestartAltIcon /> : <NavigateBeforeIcon />
            }
            disabled={activeStep === steps.length - 1}
          >
            {activeStep === 0 ? "Reset" : "Back"}
          </Button>

          <Box sx={{ display: "flex", gap: 1 }}>
            {activeStep === steps.length - 1 ? (
              <>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  startIcon={<RestartAltIcon />}
                >
                  Start Over
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  sx={{
                    background:
                      "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                  }}
                  onClick={() => alert("Save results feature coming soon!")}
                >
                  Save Results
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<NavigateNextIcon />}
                disabled={!isStepValid()}
                sx={{
                  background:
                    "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                }}
              >
                {activeStep === steps.length - 2 ? "Calculate Results" : "Next"}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default EligibilityCheckerPage;